// A friend's screens: join, tap what you had, pay the payer back.
import { compute, evenWays, fmt, nothingToPay, plain } from '../../money.ts';
import { brand, titled } from '../../brand.ts';
import { loopLink, shareChannel } from '../../loop.ts';
import { payLinks, payNoteWithLink } from '../../pay-links.ts';
import type { PublicPerson, PublicSplit } from '../../types.ts';
import { ApiError, friend, poll } from '../api.ts';
import {
  app, logo, closeSheet, copy, esc, frac, icon, initial, inputValue, leadCents, listNames, ls, openSheet, safeRender,
  privacyLink, sheetBody, smsHref, target, toast,
} from '../dom.ts';
import { onLeave } from '../router.ts';
import { claimantRows } from '../claimants.ts';
import { saved, type Membership } from '../saved.ts';
import { canInstall, installPrompt, installWithChoice, onInstallable, standalone } from '../install.ts';
import { CLIPS, homeScreenVariant, STEPS } from '../../a2hs.ts';
import { trackOnce, trackStep } from '../track.ts';
import { groupShare } from '../group-share.ts';
import { isLocked } from './lock.ts';
import { config } from './new-split.ts';
import { consent, errorScreen, feeNote, knownVenue, missingCount, optInBox, optInClick, ratedOrSkipped, surveyClick, tipPercent } from './shared.ts';
import { friendFeeLine } from '../../fee-copy.ts';
import { fee } from '../fee.ts';
import { NAME_TOO_SHORT, nameLongEnough } from '../../names.ts';

/**
 * The header next to the venue. "Alex paid" only once it's true: the host has sent totals (which
 * they do once the check is paid) or closed the split. Before that, "Alex's table".
 */
export function paidHeader(s: PublicSplit, mine: boolean): string {
  const host = s.payer.name || 'The host';
  if (isLocked(s) || s.status !== 'open') return mine ? 'you paid' : `${host} paid`;
  return mine ? 'your table' : `${host}'s table`;
}

/** The friend's fee line in their total, from src/fee-copy.ts: "+ 38¢ app fee" (C), "+ $1 thanks" (B). */
function feeRow(s: PublicSplit, cents: number): string {
  const line = friendFeeLine(s.fee?.group, cents);
  return line ? `<div class="lead accent" id="friendFee"><span>${esc(line)}</span></div>` : '';
}

/** Matches the server: one "Tell <Host> you're ready" a minute. */
const ASK_EVERY_MS = 60_000;

type View = 'claim' | 'pay' | 'claimAfterPay' | 'returned';

/** Which app this phone left for after tapping Pay, and when. Kept per split so a reload still knows. */
type Handoff = { app: 'venmo' | 'cashapp'; at: number };

export function friendView(code: string): void {
  // A link from our receipt text: /s/CODE?src=sms_receipt (or sms_reminder)#k=<id>.<token> puts this person back in their spot.
  // The token lives in the #fragment, so it never reaches a server; it's cleared from the address bar.
  const smsSrc = new URLSearchParams(location.search).get('src') || '';
  const fromSms = smsSrc === 'sms_receipt' || smsSrc === 'sms_reminder';
  const k = /^#k=([^.]+)\.(.+)$/.exec(location.hash);
  if (k) {
    saved.setMembership(code, { id: decodeURIComponent(k[1]), token: decodeURIComponent(k[2]) });
    history.replaceState(history.state, '', location.pathname + location.search);
  }
  // Which share button the link came from (?via=qr|group|text|copy), kept for this dinner so a
  // reload before joining still counts it.
  const viaNow = shareChannel(new URLSearchParams(location.search).get('via'));
  if (viaNow) ls.set('via:' + code, viaNow);
  const via = shareChannel(ls.get<string>('via:' + code, ''));
  if (fromSms) trackOnce('smsret:' + code + ':' + Math.floor(Date.now() / 600_000), 'sms_return', { code, src: smsSrc, ...(smsSrc === 'sms_reminder' && /^d(21|45)$/.test(new URLSearchParams(location.search).get('d') || '') ? { d: new URLSearchParams(location.search).get('d') as string } : {}) });
  const ui: { split: PublicSplit | null; me: Membership | null; view: View; joinError: string; nameTouched?: boolean; done: 'paid' | 'rate' | 'deals' | 'next'; returnedFrom: 'venmo' | 'cashapp' } = {
    split: null, me: saved.membership(code), view: fromSms && saved.membership(code) ? 'returned' : 'claim', joinError: '', done: 'paid', returnedFrom: 'venmo',
  };
  app.innerHTML = '<p class="loading">Loading…</p>';

  const poller = poll(code, null, (s, err) => {
    if (err || !s) return errorScreen(err?.message || 'Something went wrong. Try again.');
    ui.split = s;
    if (ui.me && !s.people.some((p) => p.id === ui.me?.id)) ui.me = null;
    safeRender(render);
  });
  onLeave(poller.stop);

  // ---------- coming back from Venmo or Cash App ----------
  // There's no way to be sent back from their apps, but this tab stays open behind them. When it
  // shows again after a Pay tap, ask "Paid? Mark me paid", then the next-time screen.
  const handoffKey = 'payHandoff:' + code;
  const onReturn = () => {
    if (document.visibilityState !== 'visible') return;
    const h = ls.get<Handoff | null>(handoffKey, null);
    if (!h || h.at === 0) return;
    const me = ui.split?.people.find((p) => p.id === ui.me?.id);
    if (!me || (me.paid && !mineDue())) { ls.set(handoffKey, null); return; }
    // Counted once per Pay tap.
    ls.set(handoffKey, { ...h, at: 0 });
    trackStep('returned_after_pay', { code, app: h.app, secs: Math.round((Date.now() - h.at) / 1000) });
    ui.returnedFrom = h.app;
    ui.view = 'returned';
    window.scrollTo(0, 0);
    safeRender(render);
  };
  const mineDue = (): number => {
    const s = ui.split;
    return s && ui.me && compute(s).people[ui.me.id] ? compute(s).people[ui.me.id].due : 0;
  };
  document.addEventListener('visibilitychange', onReturn);
  window.addEventListener('pageshow', onReturn);
  onLeave(() => { document.removeEventListener('visibilitychange', onReturn); window.removeEventListener('pageshow', onReturn); });

  const payerName = (): string => ui.split?.payer.name || '';

  /** Run a friend action, show any problem, then pull the latest state. */
  // "Tell <Host> you're ready": when this friend last asked (this page). The button rests a minute.
  let askedAt = 0;
  let askTimer: ReturnType<typeof setTimeout> | undefined;
  onLeave(() => clearTimeout(askTimer));
  async function askLock(): Promise<void> {
    if (!ui.me || Date.now() - askedAt < ASK_EVERY_MS) return;
    askedAt = Date.now();
    render();
    clearTimeout(askTimer);
    askTimer = setTimeout(render, ASK_EVERY_MS + 50);
    try { await friend.nudge(code, ui.me.token); } catch (err) {
      // 429: they already asked within the minute (say, before a reload). That still reads as sent.
      if (!(err instanceof ApiError && err.status === 429)) { askedAt = 0; toast((err as Error).message); await poller.refresh(); }
    }
  }

  async function act(fn: (token: string) => Promise<unknown>): Promise<void> {
    if (!ui.me) return;
    try { await fn(ui.me.token); } catch (err) { toast((err as Error).message); }
    await poller.refresh();
  }

  function render(): void {
    const s = ui.split;
    if (!s) return;
    document.title = titled(s.venue);
    const me = s.people.find((p) => p.id === ui.me?.id);
    if (!me) return renderJoin(s);
    if (ui.view === 'returned') return renderReturned(s, me);
    // Until the host locks it in there's no total to pay, so no pay screen.
    if (ui.view === 'pay' && isLocked(s)) return renderPay(s, me);
    // Paid: the one done screen (the same page as coming back from Venmo or the text).
    if (me.paid && ui.view !== 'claimAfterPay') return renderReturned(s, me);
    if (s.even) return renderEven(s, me);
    return renderClaim(s, me);
  }

  // ---------- join ----------

  function renderJoin(s: PublicSplit): void {
    // Spots the payer added for someone without a phone can't be claimed, so they aren't offered here.
    const others = s.people.filter((p) => !p.isPayer && !p.addedByPayer);
    // No-phone people (added by the payer) stay out of what friends see: no name, no avatar, no count.
    const shown = s.people.filter((p) => !p.addedByPayer);
    const names = shown.map((p) => p.name);
    const open = s.status === 'open';
    const methods = [s.payer.venmo && 'Venmo', s.payer.cashapp && 'Cash App', s.payer.zelle && 'Zelle'].filter((m): m is string => !!m);
    app.innerHTML = `
      <div class="bar">${logo}<span>No app needed</span></div>
      <form class="body" id="joinForm" autocomplete="on" style="padding-top:24px">
        <div class="stack">
          <span class="lbl">${[knownVenue(s) ? esc(s.venue) : '', s.date ? esc(s.date) : ''].filter(Boolean).join(' · ')}</span>
          <h1 class="h h-xl">${esc(s.payer.name)} got the bill. ${s.even ? `Split evenly, ${evenWays(s)} ways.` : open ? 'Tap what you had.' : ''}</h1>
          <p class="lede">${s.even && isLocked(s)
            ? `Your share is ${fmt(compute(s).even?.share ?? 0)}, tax and tip included${s.thanksOn ? `, plus ${fmt(s.thanksCents)} thanks for covering` : ''}. Pay ${esc(s.payer.name)} back${methods.length ? ' on ' + listNames(methods) : ''} in one tap.`
            : `You'll see your exact share, then pay ${esc(s.payer.name)} back${methods.length ? ' on ' + listNames(methods) : ''}.`}</p>
        </div>
        <div style="display:flex;align-items:center;gap:12px">
          <div class="avatars">${shown.slice(0, 5).map((p) => `<span class="av ${p.isPayer ? 'payer' : ''}">${esc(initial(p.name))}</span>`).join('')}</div>
          <span class="sub">${esc(listNames(names.slice(0, 4)))}${names.length > 4 ? ` and ${names.length - 4} more` : ''} ${names.length === 1 ? 'is' : 'are'} in${s.partySize ? ` · ${shown.length} of ${s.partySize}` : ''}</span>
        </div>
        ${open ? `
          <div class="field">
            <label for="name">Your name</label>
            <input id="name" class="input" name="name" autocomplete="given-name" style="font-size:18px;height:54px" value="${esc(saved.name())}" aria-describedby="joinError">
            <span class="hint">${s.even ? `So ${esc(s.payer.name)} knows who paid.` : 'So everyone can see what you claimed.'} Saved on this phone.</span>
          </div>
          ${config.sms ? `<div class="field">
            <label for="phone">Get a text so you don't forget to pay <span class="sub">(optional)</span></label>
            <input id="phone" class="input" name="tel" type="tel" autocomplete="tel" inputmode="tel" value="${esc(saved.phone())}">
            <span class="hint" id="phoneConsent">We'll text your receipt and a link to mark yourself paid. Msg rates may apply. Reply STOP to opt out. ${privacyLink()}</span>
            <label class="sub" style="display:flex;gap:8px;align-items:center;margin-top:6px"><input type="checkbox" id="remind" name="remind"> Also remind me next time I get the bill</label>
          </div>` : ''}` : `<div class="note">${esc(s.payer.name)} closed this split. If you joined on this phone, tap your name to see what you owe. If not, ask ${esc(s.payer.name)} to add you.</div>`}
        ${ui.joinError ? `<div class="error" id="joinError" role="alert">${esc(ui.joinError)}</div>` : ''}
        ${others.length ? `
          <div class="stack" style="gap:8px">
            <span class="sub">${open ? 'Already here? Tap your name to get back in on this phone.' : 'Who are you?'}</span>
            <div class="chips">${others.map((p) => `<button type="button" class="chip" style="flex:0 1 auto;padding:0 14px" data-act="rejoin" data-id="${esc(p.id)}">${esc(p.name)}</button>`).join('')}</div>
          </div>` : ''}
      </form>
      ${open ? `<div class="foot"><button class="btn" data-act="join">${s.even ? 'See my share' : 'Start claiming'}</button></div>` : ''}`;

    const join = async (body: { name?: string; phone?: string; remind?: boolean; personId?: string; token?: string; tz?: string }) => {
      try {
        const r = await friend.join(code, { ...body, ...(via ? { via } : {}) });
        ui.me = { id: r.personId, token: r.token };
        saved.setMembership(code, ui.me);
        ui.joinError = '';
        await poller.refresh();
        render();
      } catch (err) {
        ui.joinError = (err as Error).message;
        render();
      }
    };
    const NAME_NEEDED = 'Enter your name so everyone knows what you claimed.';
    const submit = () => {
      const name = inputValue('name');
      if (!name) { ui.joinError = NAME_NEEDED; return render(); }
      if (!nameLongEnough(name)) { ui.joinError = NAME_TOO_SHORT; return render(); }
      saved.setName(name);
      // SMS off: there's no phone box, so send no phone and keep any number saved on this phone.
      const phone = config.sms ? inputValue('phone') : '';
      if (config.sms) saved.setPhone(phone);
      void join({ name, phone, remind: !!phone && !!document.querySelector<HTMLInputElement>('#remind')?.checked, tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '' });
    };
    app.onsubmit = (e) => { e.preventDefault(); submit(); };
    // The name is checked when they leave the box or tap the button, never while typing: typing
    // clears an old error, and an empty box only gets one once they've typed in it and left.
    const nameBox = document.querySelector<HTMLInputElement>('#name');
    nameBox?.addEventListener('input', () => {
      ui.nameTouched = true;
      if (ui.joinError) { ui.joinError = ''; document.getElementById('joinError')?.remove(); }
    });
    nameBox?.addEventListener('blur', (e) => {
      if (!ui.nameTouched || nameLongEnough(nameBox.value) || ui.joinError) return;
      // Leaving the box for the button: the tap's own check says it once.
      if ((e.relatedTarget as HTMLElement | null)?.closest?.('[data-act=join]')) return;
      ui.joinError = nameBox.value.trim() ? NAME_TOO_SHORT : NAME_NEEDED;
      safeRender(render);
    });
    app.onclick = (e) => {
      const b = target(e, '[data-act]');
      if (b?.dataset.act === 'join') submit();
      else if (b?.dataset.act === 'rejoin' && b.dataset.id) {
        // Only this phone's own saved spot comes back; anyone else's name says "taken, add yourself".
        const mine = saved.membership(code);
        void join({ personId: b.dataset.id, ...(mine?.id === b.dataset.id ? { token: mine.token } : {}) });
      }
    };
  }

  // ---------- claim ----------

  function renderClaim(s: PublicSplit, me: PublicPerson): void {
    const c = compute(s);
    const mine = c.people[me.id];
    const byId = new Map(s.people.map((p) => [p.id, p]));
    const open = s.status === 'open';
    const missing = missingCount(s);

    // Items someone else put me on that I haven't answered yet.
    const marks = s.items.flatMap((it) => {
      const entry = (s.claims[it.id] || []).find((x) => x.pid === me.id);
      const by = entry && entry.by !== me.id && !entry.ack ? byId.get(entry.by) : undefined;
      return by ? [{ it, by, share: mine.items.find((i) => i.itemId === it.id) }] : [];
    });

    const leftoverLabel = (): string => {
      const lo = s.leftover || { mode: 'even' };
      if (lo.mode === 'cover') return `Leftover · ${payerName()} covered it`;
      if (lo.mode === 'assign') return `Leftover · given to ${(lo.assignTo && byId.get(lo.assignTo)?.name) || 'someone'}`;
      return 'Leftover · split across everyone';
    };
    // A tip typed in as an amount is labelled just "Tip"; show what percent of the food it came to.
    const tipLabel = s.tipLabel && !s.tipLabel.endsWith('%') && s.tipLabel !== 'Tip' ? s.tipLabel : `${tipPercent(s)}%`;

    app.innerHTML = `
      <div class="bar"><span><b style="color:var(--ink)">${esc(s.venue)}</b> · ${esc(paidHeader(s, me.isPayer))}</span><span style="display:flex;align-items:center;gap:6px"><span class="av" style="width:26px;height:26px;font-size:12px">${esc(initial(me.name))}</span>${esc(me.name)}</span></div>
      <div class="body" style="gap:10px">
        <h1 class="h" style="font-size:26px">${open ? 'Tap what you had' : 'Your final share'}</h1>
        ${open && missing ? `<p class="sub" style="margin:0">${s.people.length} of ${s.partySize} here · waiting on ${missing} more. Know who? Nudge them to scan ${esc(payerName())}'s code.</p>` : ''}
        ${!open ? `<div class="note">${esc(payerName())} closed this split, so items are locked.${mine.leftoverSub ? ` Your total includes ${fmt(mine.leftoverSub)} of items nobody claimed.` : ''}</div>` : ''}
        ${me.paid && !me.isPayer ? changeNote(mine) : ''}
        ${marks.map((m) => `
          <div class="mark">
            <span><b>${esc(m.by.name)}</b> marked you on ${esc(m.it.name)}${m.share ? (m.share.ways > 1 ? `, ${frac(m.share.ways)}` : '') + ` · ${fmt(m.share.cents)}` : ''}</span>
            ${open ? `<button data-act="unmark" data-id="${esc(m.it.id)}">Remove</button>` : ''}
            <button class="keep" data-act="keep" data-id="${esc(m.it.id)}">Keep</button>
          </div>`).join('')}
        <div style="padding-top:4px">
          ${s.items.map((it) => {
            const ids = (s.claims[it.id] || []).map((x) => x.pid).filter((id) => byId.has(id));
            const isMine = ids.includes(me.id);
            // Someone without a phone is never named to friends: they're counted as "1 other".
            const hidden = ids.filter((id) => id !== me.id && byId.get(id)?.addedByPayer).length;
            const names = ids.filter((id) => id === me.id || !byId.get(id)?.addedByPayer).map((id) => (id === me.id ? 'You' : byId.get(id)?.name || ''));
            if (hidden) names.push(hidden === 1 ? '1 other' : `${hidden} others`);
            if (!names.length && !open) names.push(leftoverLabel());
            const share = mine.items.find((i) => i.itemId === it.id);
            return `
              <div class="claim-row">
                <button class="claim" data-act="toggle" data-id="${esc(it.id)}" aria-pressed="${isMine}" ${open ? '' : 'disabled'}>
                  <span class="box">${isMine ? icon.check : ''}</span>
                  <span style="flex:1;min-width:0"><span class="nm">${esc(it.name)}</span><span class="claimers ${names.length ? '' : 'none'}">${names.length ? esc(names.join(', ')) : 'Not claimed yet'}</span></span>
                  <span class="pr"><span class="amt" style="font-size:14.5px">${plain(it.cents)}</span>${share && share.ways > 1 ? `<span class="mine amt">you ${plain(share.cents)}</span>` : ''}</span>
                </button>
                ${isMine && open ? `<button class="shared-btn" data-act="share" data-id="${esc(it.id)}">Shared?</button>` : ''}
              </div>`;
          }).join('')}
        </div>
      </div>
      <div class="foot raised">
        ${!isLocked(s) && !me.isPayer ? `
        <div>${leadCents('Your items', mine.itemsSub)}</div>
        ${waitButton(mine.items.length > 0)}` : `
        <div>
          ${leadCents('Your items', mine.itemsSub)}
          ${mine.leftoverSub ? leadCents('Your share of unclaimed items', mine.leftoverSub) : ''}
          ${leadCents(`Your ${s.taxLabel ? 'tax & fees' : 'tax'}`, mine.tax)}${feeNote(s)}
          ${leadCents(`Your tip · ${esc(tipLabel)}, set by ${me.isPayer ? 'you' : esc(payerName())}`, mine.tip)}
          ${mine.thanks ? (s.fee?.group === 'B' ? feeRow(s, mine.thanks) : leadCents(`Thanks to ${esc(payerName())} for covering`, mine.thanks, 'accent')) : ''}
          ${feeRow(s, mine.fee)}
        </div>
        ${me.isPayer
          ? `<a class="btn" href="/p/${esc(code)}" data-go>Your share is ${fmt(mine.current)} · Back to board</a>`
          : me.paid
            ? mine.due > 0 ? `<button class="btn" data-act="pay">Pay the rest · ${fmt(mine.due)}</button>` : '<button class="btn" data-act="toThanks">Done</button>'
            : nothingToPay(mine)
              ? `<div class="note" id="nothingToPay">${open ? 'Nothing to pay. Had something? Tap it above.' : `Nothing to pay. You're square with ${esc(payerName())}.`}</div>`
              : `<button class="btn" data-act="pay" ${mine.current > 0 ? '' : 'disabled'}>${mine.current > 0 ? `Pay ${esc(payerName())} ${fmt(mine.current)}` : 'Tap your items first'}</button>`}`}
      </div>`;

    app.onclick = async (e) => {
      const b = target(e, '[data-act]');
      if (!b) return;
      const itemId = b.dataset.id || '';
      switch (b.dataset.act) {
        case 'toggle': {
          // Show the tap right away, then sync.
          const on = b.getAttribute('aria-pressed') !== 'true';
          const list = (s.claims[itemId] || []).filter((x) => x.pid !== me.id);
          if (on) list.push({ pid: me.id, by: me.id });
          s.claims[itemId] = list;
          renderClaim(s, me);
          await act((t) => friend.claim(code, t, itemId, on));
          break;
        }
        case 'share': shareSheet(s, me, itemId); break;
        case 'keep': await act((t) => friend.answerMark(code, t, itemId, true)); break;
        case 'unmark': await act((t) => friend.answerMark(code, t, itemId, false)); break;
        case 'pay': ui.view = 'pay'; window.scrollTo(0, 0); render(); break;
        case 'toThanks': ui.view = 'claim'; render(); break;
        case 'askLock': await askLock(); break;
      }
    };
  }

  /** The pay button's place before lock-in: grayed out, no amount. It becomes the real one when locked; the page updates on its own. */
  function waitButton(picked = true): string {
    const sent = Date.now() - askedAt < ASK_EVERY_MS;
    // No items tapped yet: the same button, led by "Had nothing?" so a friend who had nothing knows what to tap.
    const label = `${picked ? '' : 'Had nothing? '}Tell ${esc(payerName())} you're ready`;
    return `<button class="btn" id="payWaiting" disabled aria-disabled="true" style="background:var(--line, #e5e2dc);color:var(--ink-3, #8a857c);box-shadow:none;cursor:not-allowed">Your total shows once ${esc(payerName())} asks</button>
      <button class="btn ghost" id="askLock" data-act="askLock" ${sent ? 'disabled aria-disabled="true"' : ''} style="margin-top:8px;min-height:40px;font-size:14px;background:transparent;color:var(--ink-2, #5c584f);box-shadow:none;border:1px solid var(--line, #e5e2dc)${sent ? ';opacity:.6;cursor:default' : ''}">${label}</button>
      ${sent ? `<p class="hint" id="askSent" role="status" style="text-align:center;margin:6px 0 0">Sent. ${esc(payerName())} will see it.</p>` : ''}`;
  }

  // ---------- split evenly: no items to tap, just your share ----------

  function renderEven(s: PublicSplit, me: PublicPerson): void {
    const c = compute(s);
    const mine = c.people[me.id];
    const ways = c.even?.ways ?? evenWays(s);
    if (!isLocked(s) && !me.isPayer) {
      app.innerHTML = `
      <div class="bar"><span><b style="color:var(--ink)">${esc(s.venue)}</b> · ${esc(paidHeader(s, me.isPayer))}</span><span style="display:flex;align-items:center;gap:6px"><span class="av" style="width:26px;height:26px;font-size:12px">${esc(initial(me.name))}</span>${esc(me.name)}</span></div>
      <div class="body">
        <div><span class="lbl">Your share · split evenly, ${ways} ways</span></div>
      </div>
      <div class="foot">${waitButton()}</div>`;
      app.onclick = (e) => { if (target(e, '[data-act="askLock"]')) void askLock(); };
      return;
    }
    app.innerHTML = `
      <div class="bar"><span><b style="color:var(--ink)">${esc(s.venue)}</b> · ${esc(paidHeader(s, me.isPayer))}</span><span style="display:flex;align-items:center;gap:6px"><span class="av" style="width:26px;height:26px;font-size:12px">${esc(initial(me.name))}</span>${esc(me.name)}</span></div>
      <div class="body">
        <div><span class="lbl">Your share · split evenly, ${ways} ways</span><div class="big-amt" style="font-size:44px">${fmt(mine.current)}</div></div>
        ${me.paid && !me.isPayer ? changeNote(mine) : ''}
        <div class="ledger">
          ${leadCents(`Food &amp; drinks · 1 of ${ways}`, mine.sub)}
          ${leadCents(`${s.taxLabel ? 'Tax &amp; fees' : 'Tax'}`, mine.tax)}${feeNote(s)}
          ${leadCents(`Tip, set by ${me.isPayer ? 'you' : esc(payerName())}`, mine.tip)}
          ${mine.thanks ? (s.fee?.group === 'B' ? feeRow(s, mine.thanks) : leadCents(`Thanks to ${esc(payerName())} for covering`, mine.thanks, 'accent')) : ''}
          ${feeRow(s, mine.fee)}
          ${leadCents('Your share', mine.current, 'total')}
        </div>
        <p class="sub" style="margin:0">The whole bill is ${fmt(c.total)}. ${esc(me.isPayer ? 'You' : payerName())} split it evenly, so nobody has to tap items.</p>
      </div>
      <div class="foot">
        ${me.isPayer
          ? `<a class="btn" href="/p/${esc(code)}" data-go>Back to board</a>`
          : me.paid
            ? mine.due > 0 ? `<button class="btn" data-act="pay">Pay the rest · ${fmt(mine.due)}</button>` : '<button class="btn" data-act="toThanks">Done</button>'
            : `<button class="btn" data-act="pay">Pay ${esc(payerName())} ${fmt(mine.current)}</button>`}
      </div>`;

    app.onclick = (e) => {
      const b = target(e, '[data-act]');
      if (b?.dataset.act === 'pay') { ui.view = 'pay'; window.scrollTo(0, 0); render(); }
      else if (b?.dataset.act === 'toThanks') { ui.view = 'claim'; render(); }
    };
  }

  function shareSheet(s: PublicSplit, me: PublicPerson, itemId: string): void {
    const it = s.items.find((x) => x.id === itemId);
    if (!it) return;
    // Hidden no-phone people can't be picked here, but if they're already on the item they stay on it.
    const sel = new Set((s.claims[itemId] || []).map((x) => x.pid).filter((pid) => pid !== me.id));
    const others = claimantRows(s.people, me.id, [...sel]);
    const draw = () => {
      const box = sheetBody();
      if (!box) return;
      const n = 1 + sel.size;
      box.innerHTML = `
        <div class="grab"></div>
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px"><h2 class="h" style="font-size:24px">Who shared the ${esc(it.name)}?</h2><span class="amt">${plain(it.cents)}</span></div>
        <button class="check" aria-checked="true" disabled><span class="box">${icon.check}</span>You</button>
        ${others.length ? others.map((r) => r.locked
          ? `<button class="check" aria-checked="true" disabled><span class="box">${icon.check}</span>${esc(r.label)}</button>`
          : `<button class="check" role="checkbox" aria-checked="${sel.has(r.id)}" data-pid="${esc(r.id)}"><span class="box">${sel.has(r.id) ? icon.check : ''}</span>${esc(r.label)}</button>`).join('') : '<p class="sub">Nobody else has joined yet. Once they do, you can pick them here.</p>'}
        <div style="display:flex;justify-content:space-between;align-items:baseline"><b>${n === 1 ? 'Just you' : `Split ${n} ways`}</b><span class="amt" style="font-weight:700;font-size:18px">${fmt(Math.round(it.cents / n))} each</span></div>
        <p class="sub" style="margin:0">The people you pick see it on their own page, marked by you, and can remove it.</p>
        <button class="btn" data-act="done">Done</button>`;
    };
    openSheet('', async (e) => {
      const pick = target(e, '[data-pid]');
      if (pick?.dataset.pid) {
        const pid = pick.dataset.pid;
        if (sel.has(pid)) sel.delete(pid); else sel.add(pid);
        return draw();
      }
      if (target(e, '[data-act=done]')) {
        closeSheet();
        await act((t) => friend.share(code, t, itemId, [...sel]));
      }
    });
    draw();
  }

  // ---------- pay ----------

  /**
   * "I paid <host>" is a small link until they've tapped Venmo or Cash App (or copied Zelle), then
   * the big button. Zelle-only payers have no app button, so it's big from the start.
   */
  let tapped = false;
  const tappedPay = (): boolean => tapped || !!ls.get<Handoff | null>(handoffKey, null) || !(ui.split && (ui.split.payer.venmo || ui.split.payer.cashapp));
  function promotePaid(): void {
    tapped = true;
    const b = app.querySelector<HTMLButtonElement>('[data-act=paid]');
    if (b) { b.className = 'btn'; b.style.textDecoration = ''; }
  }

  function renderPay(s: PublicSplit, me: PublicPerson): void {
    const mine = compute(s).people[me.id];
    // Paid already and the share went up: this is just the rest.
    const owed = me.paid ? mine.due : mine.current;
    // Venmo's note carries a "Start a split" link with the payer's code; never the restaurant's name.
    const links = payLinks(s.payer, owed, payNoteWithLink(location.origin + loopLink(s.code, s.payerRef, 'pay_note')));
    app.innerHTML = `
      <div class="bar"><button class="back" data-act="back">${icon.back}${s.even ? 'Back' : 'Edit items'}</button><span>${knownVenue(s) ? esc(s.venue) : ''}</span></div>
      <div class="body">
        <div><span class="lbl">${me.paid ? `The rest you owe ${esc(payerName())}` : `You owe ${esc(payerName())}`}</span><div class="big-amt" style="font-size:44px">${fmt(owed)}</div>${me.paid ? `<div class="sub">Your share went up to ${fmt(mine.current)} after you paid ${fmt(me.paid.cents)}.</div>` : ''}</div>
        <div class="ledger">
          ${mine.items.map((i) => leadCents(esc(i.ways > 1 ? `${i.name} · ${frac(i.ways)}` : i.name), i.cents)).join('')}
          ${s.even ? leadCents(`Food &amp; drinks · 1 of ${evenWays(s)}`, mine.sub) : ''}
          ${mine.leftoverSub ? leadCents('Share of unclaimed items', mine.leftoverSub) : ''}
          ${leadCents(`Your ${s.taxLabel ? 'tax & fees' : 'tax'}`, mine.tax)}${feeNote(s)}
          ${mine.tip ? leadCents('Your tip', mine.tip) : ''}
          ${mine.thanks ? (s.fee?.group === 'B' ? feeRow(s, mine.thanks) : leadCents(`Thanks to ${esc(payerName())} for covering`, mine.thanks, 'accent')) : ''}
          ${feeRow(s, mine.fee)}
          ${me.paid ? leadCents('Already paid', -me.paid.cents) : ''}
          ${leadCents(me.paid ? 'The rest' : 'Total', owed, 'total')}
        </div>
        <div class="stack">
          ${links.map((l) => `<a class="pay ${l.app}" href="${esc(l.url)}" target="_blank" rel="noopener" data-pay="${l.app}">Pay with ${l.label}<small>${esc(l.handle)}</small></a>`).join('')}
          ${s.payer.zelle ? `<div class="copy-row"><div style="flex:1;min-width:0"><div class="sub">Zelle ${esc(s.payer.name)} ${fmt(owed)} at</div><div class="v">${esc(s.payer.zelle)}</div></div><button class="ghost-btn" data-act="copyZelle">Copy</button></div>` : ''}
          <p class="sub center" style="margin:0">Opens the app with ${fmt(owed)}${links.some((l) => l.app === 'venmo') ? ' and a note' : ''} filled in. The money goes straight to ${esc(payerName())}.</p>
          ${links.length ? '<p class="center" id="comeBack" style="margin:0;font-weight:600">After you pay, come back here to finish ✓</p>' : ''}
        </div>
        <div class="card stack" id="nextTimePay" style="gap:8px">
          <span class="sub">Next time you get the bill, split it here</span>
          <button class="btn2" data-act="groupShare">Save this for next time</button>
        </div>
      </div>
      <div class="foot">
        <button class="${tappedPay() ? 'btn' : 'link center'}" data-act="paid"${tappedPay() ? '' : ' style="text-decoration:none"'}>${me.paid ? 'I paid the rest' : `I paid ${esc(payerName())}`}</button>
        <button class="link center" data-act="paidOther" style="text-decoration:none">Paid another way, like cash</button>
      </div>`;

    app.onclick = async (e) => {
      const p = target<HTMLAnchorElement>(e, '[data-pay]');
      if (p) {
        // Don't block the link: mark "paying" on the way out, and remember we handed off.
        const appName = p.dataset.pay === 'cashapp' ? 'cashapp' : 'venmo';
        ls.set(handoffKey, { app: appName, at: Date.now() } satisfies Handoff);
        if (ui.me) void friend.paying(code, ui.me.token, appName).catch(() => {});
        promotePaid();
        return;
      }
      const b = target<HTMLButtonElement>(e, '[data-act]');
      if (!b) return;
      const a = b.dataset.act;
      if (a === 'copyZelle') promotePaid();
      if (a === 'groupShare') return void groupShare(s, 'friend');
      if (a === 'back') { ui.view = 'claimAfterPay'; render(); }
      else if (a === 'copyZelle') void copy(s.payer.zelle);
      else if (a === 'paid' || a === 'paidOther') {
        b.disabled = true;
        ui.view = 'claim';
        await act((t) => friend.setPaid(code, t, true, a === 'paid' ? 'app' : 'other'));
        window.scrollTo(0, 0);
      }
    };
  }

  /**
   * The one come-back page, from Venmo or from the receipt text. Final copy per Product:
   * 1) "Paid <host> back?" with one big "Yes, I paid" and a small "Not yet? Pay now" (same deep link).
   * 2) After Yes: "Done. <host> can see you're paid." and their own referral code (or the phone box).
   * 3) Last, the Home Screen tip behind "Show me how", hidden when already opened from the Home Screen.
   */
  let codeInfo: { code?: string; link?: string; needPhone?: boolean; off?: boolean } | null = null;
  let howOpen = false;
  let installWatch = false;
  function renderReturned(s: PublicSplit, me: PublicPerson): void {
    const host = esc(payerName());
    const mine = compute(s).people[me.id];
    const square = !!me.paid && !(mine && mine.due > 0);
    const owed = mine ? (me.paid ? mine.due : mine.current) : 0;
    const links = payLinks(s.payer, owed, payNoteWithLink(location.origin + loopLink(s.code, s.payerRef, 'pay_note')));
    const pay = links.find((l) => l.app === ui.returnedFrom) || links[0];
    // Pricing off: a code earns nothing, so the code card isn't shown or asked for.
    if (square && !codeInfo && ui.me && fee.pricing) {
      const token = ui.me.token;
      void friend.myCode(code, token).then((r) => { codeInfo = r; render(); }).catch(() => {});
    }
    const codeCard = !square || !fee.pricing ? '' : !codeInfo ? '<div class="card" id="codeCard"><p class="sub" style="margin:0">Getting your code…</p></div>'
      : codeInfo.needPhone ? `
        <div class="card stack" id="codeCard" style="gap:10px">
          <h2 class="h" style="font-size:22px">Add your number to get your code</h2>
          <p class="sub" style="margin:0">No subscription. Pay per split. Friends who start a split with your code earn you a split with no fee.</p>
          <input class="input" id="codePhone" type="tel" autocomplete="tel" inputmode="tel" placeholder="Your phone number" aria-label="Your phone number">
          <span class="hint" id="codeConsent">Your number keeps your code and those splits yours. We don't text you from this box. ${privacyLink()}</span>
          <button class="btn2" data-act="savePhone">Get my code</button>
        </div>`
      : codeInfo.code ? `
        <div class="card stack" id="codeCard" style="gap:10px">
          <h2 class="h" style="font-size:22px">Your code: ${esc(codeInfo.code)}</h2>
          <p class="sub" style="margin:0">When a friend starts their first split with it and gets paid back, you get a split with no fee.</p>
          <input class="input" readonly value="${esc(codeInfo.link || '')}" aria-label="Your link">
          <button class="btn2" data-act="shareCode">Share my code</button>
        </div>` : '';
    const how = standalone() ? '' : homeScreenTip();
    app.innerHTML = `
      <div class="bar">${logo}<span>${knownVenue(s) ? esc(s.venue) : ''}</span></div>
      <div class="body" style="gap:22px">
        <div class="stack">
          ${me.paid
            ? `${square
              ? `<div class="done-mark settled">${icon.bigCheck}</div>
          <h1 class="h h-xl settled">Settled.</h1>
          <p class="lede">${host}'s been paid. The group chat can go back to memes.</p>`
              : `<div class="done-mark">${icon.bigCheck}</div>
          <h1 class="h h-xl">Almost done</h1>`}
          <p class="sub">${host} can see you paid ${fmt(me.paid.cents)}.</p>
          ${mine && (mine.due || mine.refund) ? changeNote(mine) : ''}`
            : `<h1 class="h h-xl">Paid ${host} back?</h1>
          <p class="lede">Tap below so ${host} doesn't have to chase you down.</p>
          <button class="btn" data-act="markPaid" style="min-height:64px;font-size:20px">Yes, I paid</button>
          ${pay ? `<a class="link center" href="${esc(pay.url)}" target="_blank" rel="noopener" data-pay="${pay.app}" style="text-decoration:none">Not yet? Pay now</a>` : ''}`}
        </div>
        ${codeCard}
        ${how}
        ${square && wants('deals', s) ? `
        <div class="card stack" id="dealsCard" style="gap:10px">
          <h2 class="h" style="font-size:22px">Want deals from ${esc(s.venue)}?</h2>
          <p class="sub" style="margin:0;text-align:left">${esc(consent(s.venue))} We never sell your number. ${privacyLink()}</p>
          ${optInBox(s.venue)}
          <input id="optPhone" class="input" type="tel" inputmode="tel" autocomplete="tel" placeholder="Your phone number" value="${esc(saved.phone())}" aria-label="Phone number for offers">
          <button class="btn2" data-act="optIn">Text me deals</button>
          <button class="link center" data-act="noDeals" style="text-decoration:none">No thanks</button>
        </div>` : ''}
        ${me.paid ? '<button class="link center" data-act="wrong" style="text-decoration:none">Something wrong?</button>' : ''}
      </div>`;
    if (square && wants('deals', s)) trackOnce('optin:' + s.code, 'optin_shown', { code: s.code });
    wireClips();
    app.onclick = async (e) => {
      const p = target<HTMLAnchorElement>(e, '[data-pay]');
      if (p) {
        const appName = p.dataset.pay === 'cashapp' ? 'cashapp' : 'venmo';
        ls.set(handoffKey, { app: appName, at: Date.now() } satisfies Handoff);
        if (ui.me) void friend.paying(code, ui.me.token, appName).catch(() => {});
        return;
      }
      if (await optInClick(e, s, ui.me?.token, render)) return;
      const b = target<HTMLButtonElement>(e, '[data-act]');
      if (!b) return;
      const a = b.dataset.act;
      if (a === 'noDeals') { ls.set('dealsNo:' + code, true); return render(); }
      if (a === 'pay') { ui.view = 'pay'; window.scrollTo(0, 0); return render(); }
      if (a === 'wrong') return somethingWrong(s);
      if (a === 'markPaid') {
        b.disabled = true;
        ls.set(handoffKey, null);
        trackStep('return_page_paid', { code: s.code });
        await act((t) => friend.setPaid(code, t, true, 'app'));
        window.scrollTo(0, 0);
        return;
      }
      if (a === 'savePhone' && ui.me) {
        const phone = inputValue('codePhone');
        if (!phone) return toast('Add your number first.');
        b.disabled = true;
        saved.setPhone(phone);
        try { codeInfo = await friend.myCode(code, ui.me.token, phone); } catch (err) { b.disabled = false; return toast((err as Error).message); }
        return render();
      }
      if (a === 'shareCode' && codeInfo?.link) {
        const text = `Use my code ${codeInfo.code} on ${brand.name} next time you get the bill: ${codeInfo.link}`;
        if (navigator.share) {
          try { await navigator.share({ text }); trackStep('return_page_code_shared', { code: s.code, how: 'share' }); } catch { /* closed */ }
        } else { trackStep('return_page_code_shared', { code: s.code, how: 'copy' }); await copy(codeInfo.link); }
        return;
      }
      if (a === 'showHow') {
        howOpen = true;
        trackStep('return_page_homescreen_opened', { code: s.code });
        return render();
      }
      if (a === 'install') {
        if ((await installWithChoice()) === 'accepted') trackStep('return_page_homescreen_installed', { code: s.code });
        return render();
      }
    };
    if (!standalone() && !canInstall() && !installWatch) {
      installWatch = true;
      onInstallable(() => render());
    }
  }

  function somethingWrong(s: PublicSplit): void {
    openSheet(`
      <h2 class="h" style="margin:4px 0 14px">What's wrong?</h2>
      <div class="stack">
        <button class="btn2" data-act="items">${s.even ? 'Show my share' : 'Show what I had'}</button>
        <button class="btn2" data-act="undo">My payment didn't go through</button>
        <button class="link center" data-close>Never mind</button>
      </div>`, async (ev) => {
      const x = target(ev, '[data-act]')?.dataset.act;
      if (!x) return;
      closeSheet();
      if (x === 'items') { ui.view = 'claimAfterPay'; render(); }
      else if (x === 'undo') { ui.view = 'pay'; codeInfo = null; await act((t) => friend.setPaid(code, t, false)); }
    });
  }

  /** The Home Screen tip: last on the page, closed until "Show me how", steps for this phone only. */
  function homeScreenTip(): string {
    const head = `<h2 class="h" style="font-size:22px">Put ${esc(brand.name)} on your Home Screen</h2>`;
    if (!howOpen) return `<div class="card stack" id="homeTip" style="gap:10px">${head}<button class="btn2" data-act="showHow">Show me how</button></div>`;
    const v = homeScreenVariant(navigator.userAgent);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const clip = (src: string, step: string) => `
      <figure class="stack" style="gap:8px;margin:0">
        <video class="a2hs-clip" src="${esc(src)}" poster="${esc(src.replace(/\.mp4$/, '.jpg'))}" playsinline muted loop ${reduce ? 'preload="metadata"' : 'autoplay'} style="width:100%;border-radius:12px"></video>
        <figcaption class="a2hs-step">${esc(step)}</figcaption>
      </figure>`;
    let body: string;
    if (v.kind === 'open-elsewhere') body = `<p class="a2hs-step" id="openElsewhere"><b>${esc(v.step)}</b></p><p class="sub" style="margin:0">This app's browser can't add it to your Home Screen.</p>`;
    else if (v.kind === 'desktop') body = clip(CLIPS.ios, STEPS.ios) + clip(CLIPS.android, STEPS.android);
    else {
      body = clip(v.clip, v.step);
      if (v.kind === 'android' && canInstall()) body = `<button class="btn" data-act="install">Add to Home Screen</button>` + body;
    }
    return `<div class="card stack" id="homeTip" style="gap:12px">${head}${body}</div>`;
  }

  /** A clip that isn't there yet (placeholder until real recordings) just disappears; its text stays. */
  function wireClips(): void {
    app.querySelectorAll<HTMLVideoElement>('video.a2hs-clip').forEach((v) => {
      v.addEventListener('error', () => { v.hidden = true; v.style.display = 'none'; }, { once: true });
      if (v.error || v.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) { v.hidden = true; v.style.display = 'none'; }
    });
  }

  /** After paying: square, or the share changed and here's the difference. */
  function changeNote(mine: { current: number; due: number; refund: number; paid: { cents: number } | null }): string {
    if (mine.due > 0) return `<div class="alert" style="flex-direction:column;align-items:stretch;gap:10px"><span>Your share went up ${fmt(mine.due)} after you paid (to ${fmt(mine.current)}), because the last items were sorted out.</span><button class="small-btn" data-act="pay" style="align-self:flex-start">Pay the rest · ${fmt(mine.due)}</button></div>`;
    if (mine.refund > 0) return `<div class="note">Your share went down to ${fmt(mine.current)} after you paid. ${esc(payerName())} owes you ${fmt(mine.refund)} back and can see it.</div>`;
    return mine.paid ? `<div class="note">You paid ${fmt(mine.paid.cents)}. You're square.</div>` : '';
  }

  // ---------- after paying: one done screen ----------
  // Paid, then their code, then the Home Screen tip, then (with a restaurant name) deals by text.

  const dealsDeclined = (): boolean => ls.get<boolean>('dealsNo:' + code, false);
  const wants = (step: 'deals', s: PublicSplit): boolean => step === 'deals' && knownVenue(s) && !saved.optedIn(s.code) && !dealsDeclined();

}
